import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, ROLE_LABELS } from '../lib/auth';
import { globalSearch } from '../lib/api';
import { cn } from '../lib/utils';
import {
    House,
    Package,
    Users,
    Upload,
    SignOut,
    MagnifyingGlass,
    List,
    Moon,
    Sun,
    Gear,
    CaretDown,
    FileText,
    PuzzlePiece,
    Tag,
    Shield,
    GridFour,
    Truck,
    Image as ImageIcon,
    AddressBook,
    Sliders,
    ArrowsLeftRight,
} from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../components/ui/avatar';

const navItems = [
    { path: '/', label: 'Dashboard', icon: House, perm: 'view_dashboard' },
    { path: '/products', label: 'Products', icon: Package, perm: 'view_products' },
    { path: '/customers', label: 'Customers', icon: AddressBook, perm: 'view_customers' },
    { path: '/quotes', label: 'Quotations', icon: FileText, perm: 'view_all_quotes', altPerm: 'view_own_quotes' },
    { path: '/custom-products', label: 'Custom Products', icon: PuzzlePiece, perm: 'view_custom_products' },
    { path: '/sheet-optimizer', label: 'Sheet Optimizer', icon: GridFour, perm: 'use_sheet_optimizer' },
    { path: '/challans', label: 'Challans', icon: Truck, perm: 'view_challans' },
    { path: '/admin/categories', label: 'Categories', icon: Tag, perm: 'manage_categories' },
    { path: '/admin/config', label: 'Config Manager', icon: Sliders, perm: 'manage_config' },
    { path: '/admin/roles', label: 'Role Permissions', icon: Shield, perm: 'manage_users' },
    { path: '/admin/permissions', label: 'Access Control', icon: Gear, perm: 'manage_users' },
    { path: '/users', label: 'Users', icon: Users, perm: 'manage_users' },
    { path: '/customers/migrate', label: 'Customer Migration', icon: ArrowsLeftRight, perm: 'manage_customer_migration' },
    { path: '/import', label: 'Data Import', icon: Upload, perm: 'import_data' },
    { path: '/import/images', label: 'Image Import', icon: ImageIcon, perm: 'import_images' },
];

export function Layout({ children }) {
    const { user, logout, isAdmin, permissions, can } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchOpen, setSearchOpen] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [darkMode, setDarkMode] = useState(false);
    const searchTimerRef = useRef(null);

    useEffect(() => {
        const saved = localStorage.getItem('darkMode');
        if (saved === 'true') {
            setDarkMode(true);
            document.documentElement.classList.add('dark');
        }
    }, []);

    const toggleDarkMode = () => {
        const newMode = !darkMode;
        setDarkMode(newMode);
        localStorage.setItem('darkMode', newMode.toString());
        document.documentElement.classList.toggle('dark', newMode);
    };

    const handleSearch = useCallback((e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
            setSearchOpen(false);
        }
    }, [searchQuery, navigate]);

    const handleSearchInput = useCallback((val) => {
        setSearchQuery(val);
        clearTimeout(searchTimerRef.current);
        if (val.length >= 2) {
            searchTimerRef.current = setTimeout(async () => {
                try {
                    const res = await globalSearch(val);
                    setSearchResults(res.data.results || []);
                    setSearchOpen(true);
                } catch { setSearchResults([]); }
            }, 300);
        } else {
            setSearchResults([]);
            setSearchOpen(false);
        }
    }, []);

    const handleKeyDown = useCallback((e) => {
        if (e.key === '/' && !e.target.closest('input, textarea')) {
            e.preventDefault();
            document.getElementById('global-search')?.focus();
        }
    }, []);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const filteredNavItems = navItems.filter(item => 
        can(item.perm) || (item.altPerm && can(item.altPerm))
    );

    const userInitials = user?.name
        ?.split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2) || '??';

    return (
        <div className="flex h-screen overflow-hidden bg-background">
            {/* Sidebar */}
            <aside 
                className={cn(
                    "fixed inset-y-0 left-0 z-40 flex flex-col border-r border-border bg-card transition-transform duration-200",
                    sidebarOpen ? "w-64" : "w-16"
                )}
            >
                {/* Logo */}
                <div className="flex h-16 items-center gap-3 border-b border-border px-4">
                    <img 
                        src="/lamikraft_logo_header.png" 
                        alt="Lamikraft" 
                        className={cn("object-contain", sidebarOpen ? "h-10 max-w-[140px]" : "h-8 w-8")}
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                    />
                    <div className="hidden h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground font-semibold text-sm">
                        LK
                    </div>
                    {sidebarOpen && (
                        <div className="flex flex-col">
                            <span className="text-xs text-muted-foreground">SKU Intelligence</span>
                        </div>
                    )}
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto p-3 scrollbar-thin">
                    <ul className="space-y-1">
                        {filteredNavItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === item.path || 
                                (item.path !== '/' && location.pathname.startsWith(item.path));
                            
                            return (
                                <li key={item.path}>
                                    <Link
                                        to={item.path}
                                        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                                        className={cn('sidebar-link', isActive && 'active')}
                                    >
                                        <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                                        {sidebarOpen && <span>{item.label}</span>}
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </nav>

                {/* Sidebar footer */}
                <div className="border-t border-border p-3">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        data-testid="toggle-sidebar"
                    >
                        <List size={20} />
                        {sidebarOpen && <span className="ml-3">Collapse</span>}
                    </Button>
                </div>
            </aside>

            {/* Main content */}
            <div className={cn(
                "flex flex-1 flex-col transition-all duration-200",
                sidebarOpen ? "ml-64" : "ml-16"
            )}>
                {/* Header */}
                <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-border bg-card px-6">
                    {/* Search */}
                    <form onSubmit={handleSearch} className="flex-1 max-w-md relative">
                        <div className="relative">
                            <MagnifyingGlass 
                                size={18} 
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
                            />
                            <Input
                                id="global-search"
                                data-testid="global-search-input"
                                type="search"
                                placeholder="Search products, quotes, challans..."
                                className="pl-10 pr-12"
                                value={searchQuery}
                                onChange={(e) => handleSearchInput(e.target.value)}
                                onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
                                onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
                            />
                            <kbd className="kbd absolute right-3 top-1/2 -translate-y-1/2">/</kbd>
                        </div>
                        {searchOpen && searchResults.length > 0 && (
                            <div className="absolute top-full mt-1 left-0 right-0 bg-card border border-border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto" data-testid="global-search-results">
                                {searchResults.map((r, i) => (
                                    <Link
                                        key={`${r.type}-${r.id}-${i}`}
                                        to={r.url}
                                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors border-b border-border/30 last:border-0"
                                        onMouseDown={(e) => { e.preventDefault(); navigate(r.url); setSearchOpen(false); setSearchQuery(''); }}
                                        data-testid={`search-result-${i}`}
                                    >
                                        <span className="text-[10px] font-semibold uppercase text-muted-foreground bg-muted px-1.5 py-0.5 rounded w-16 text-center flex-shrink-0">
                                            {r.type === 'custom_product' ? 'Custom' : r.type}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium truncate">{r.title}</div>
                                            {r.subtitle && <div className="text-xs text-muted-foreground truncate">{r.subtitle}</div>}
                                        </div>
                                        {r.meta && <span className="text-[10px] text-muted-foreground flex-shrink-0">{r.meta}</span>}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </form>

                    <div className="flex items-center gap-2">
                        {/* Dark mode toggle */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleDarkMode}
                            data-testid="dark-mode-toggle"
                        >
                            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                        </Button>

                        {/* User menu */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button 
                                    variant="ghost" 
                                    className="flex items-center gap-2 px-2"
                                    data-testid="user-menu-trigger"
                                >
                                    <Avatar className="h-8 w-8">
                                        <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                                            {userInitials}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="hidden md:flex flex-col items-start">
                                        <span className="text-sm font-medium">{user?.name}</span>
                                        <span className="text-xs text-muted-foreground">
                                            {ROLE_LABELS[user?.role]}
                                        </span>
                                    </div>
                                    <CaretDown size={16} className="text-muted-foreground" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem asChild>
                                    <Link to="/settings" className="flex items-center gap-2">
                                        <Gear size={16} />
                                        Settings
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                    onClick={logout}
                                    className="text-destructive focus:text-destructive"
                                    data-testid="logout-button"
                                >
                                    <SignOut size={16} className="mr-2" />
                                    Sign out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                {/* Page content */}
                <main className="flex-1 overflow-y-auto p-6 scrollbar-thin">
                    {children}
                </main>
            </div>
        </div>
    );
}
